import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { HeaderComponent } from '@shared/components/header/header.component';
import { TranslatePipe } from '@ngx-translate/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { NavItem } from '@shared/interfaces/nav/nav-item';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DashboardService, PriceStatus } from '@app/api/dmc';

const STORAGE_KEY = 'dashboard.lastTab';

const KNOWN_TABS = new Set([
  'tidsserier',
  'serviceydelser',
  'maalepunkter',
  'priselementer',
  'system',
]);

@Component({
  selector: 'app-dashboard',
  imports: [
    HeaderComponent,
    TranslatePipe,
    RouterOutlet,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css'
})
export class DashboardComponent implements OnInit, OnDestroy {

  private readonly priceElementFailed = signal(false);

  public navItems: NavItem[] = [
    { title: 'dashboard.overview.timeseries.title',    route: ['/dashboard', 'tidsserier'] },
    { title: 'dashboard.overview.services.title',      route: ['/dashboard', 'serviceydelser'] },
    { title: 'dashboard.overview.meteringpoints.title', route: ['/dashboard', 'maalepunkter'] },
    { title: 'dashboard.overview.priceelements.title', route: ['/dashboard', 'priselementer'], dot: () => this.priceElementFailed() },
    { title: 'dashboard.system.title',                 route: ['/dashboard', 'system'] },
    { title: 'dashboard.datahub.communication.title',  route: ['/dashboard', 'datahub'] },
  ];

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly router: Router,
    private readonly dashboardService: DashboardService,
  ) {}

  public ngOnInit(): void {
    // Persist the active tab on every dashboard navigation
    this.subscriptions.add(
      this.router.events.pipe(
        filter(e => e instanceof NavigationEnd),
      ).subscribe((e: NavigationEnd) => {
        const segment = e.urlAfterRedirects.split('/')[2]?.split('?')[0];
        if (segment && KNOWN_TABS.has(segment)) {
          localStorage.setItem(STORAGE_KEY, segment);
        }
      })
    );

    // Show alert dot on the price elements tab if automations have failed or price status is not green
    this.subscriptions.add(
      this.dashboardService.dashboardQueryFailedAutomationsCountPost().subscribe({
        next: (result) => {
          if ((result.totalHitCount ?? 0) > 0) {
            this.priceElementFailed.set(true);
          }
        },
      })
    );

    this.subscriptions.add(
      this.dashboardService.dashboardGetPriceStatusGet().subscribe({
        next: (result) => {
          if (result.priceStatus && result.priceStatus !== PriceStatus.Green) {
            this.priceElementFailed.set(true);
          }
        },
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** Called by the router when the bare /dashboard path is hit (path: '') */
  public static resolveLastTab(): string {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && KNOWN_TABS.has(saved) ? saved : 'tidsserier';
  }
}
